/**
 * OpenAI Integration — the provider layer.
 *
 * A DesignProvider is anything with `generate(request, options)` returning the
 * raw JSON string the model produced. OpenAI, Claude, Gemini and any future
 * provider satisfy exactly this contract; only the `provider` field on the
 * request differs.
 *
 * Every provider reaches the model through the secure, admin-authenticated
 * backend proxy, which attaches the server-side key. No API key exists in the
 * frontend, and none ever will.
 *
 * @typedef {Object} DesignProvider
 * @property {string} id
 * @property {string} provider
 * @property {boolean} supportsStreaming
 * @property {(request: Object, options?: Object) => Promise<string>} generate
 */

import {
  PROVIDERS, GENERATE_ENDPOINT, ADMIN_TOKEN_KEY, DEFAULT_TRANSPORT, RETRYABLE_STATUSES,
} from './openaiSchema';

/** Thrown for any transport failure, carrying what the caller needs to react. */
export class DesignProviderError extends Error {
  constructor(message, { status = null, retryable = false, cause = null } = {}) {
    super(message);
    this.name = 'DesignProviderError';
    this.status = status;
    this.retryable = retryable;
    this.cause = cause;
  }
}

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const readToken = () => {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(ADMIN_TOKEN_KEY)) || '';
  } catch {
    return ''; // storage unavailable (SSR, privacy mode) — the proxy will reject us
  }
};

/**
 * Create a provider that reaches `provider` through the backend proxy.
 *
 * @param {string} provider  one of PROVIDERS.
 * @param {Object} [config]
 * @param {Function} [config.fetchImpl]  injected for testing.
 * @param {string} [config.apiUrl]
 * @param {string} [config.endpoint]
 * @param {number} [config.timeoutMs]
 * @param {number} [config.retries]      attempts after the first.
 * @param {number} [config.backoffMs]    doubled per attempt; no jitter, so retries stay deterministic.
 * @returns {DesignProvider}
 */
export const createBackendProvider = (provider = 'openai', config = {}) => {
  const name = PROVIDERS.includes(provider) ? provider : 'openai';
  const {
    fetchImpl,
    apiUrl = process.env.REACT_APP_BACKEND_URL || '',
    endpoint = GENERATE_ENDPOINT,
    timeoutMs = DEFAULT_TRANSPORT.timeoutMs,
    retries = DEFAULT_TRANSPORT.retries,
    backoffMs = DEFAULT_TRANSPORT.backoffMs,
  } = config;

  const doFetch = (...args) => (fetchImpl || fetch)(...args);

  /** One attempt, bounded by `timeoutMs`. */
  const attempt = async (request, signal) => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller && timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
    // A caller-supplied signal cancels us too.
    const onAbort = () => controller && controller.abort();
    if (signal && controller) signal.addEventListener('abort', onAbort);

    try {
      const res = await doFetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        signal: controller ? controller.signal : signal,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${readToken()}` },
        body: JSON.stringify({
          provider: name,
          // No model: the server picks it. See openaiSchema.
          count: request.count,
          messages: request.messages,
          max_tokens: request.max_tokens,
          response_format: request.response_format,
          // The proxy binds this to the provider's native structured output, so
          // the schema constrains generation rather than merely asking for it.
          response_schema: request.response_schema,
        }),
      });

      if (!res.ok) {
        let detail = `Design generation failed (${res.status})`;
        try { detail = (await res.json()).detail || detail; } catch { /* keep the status message */ }
        throw new DesignProviderError(detail, { status: res.status, retryable: RETRYABLE_STATUSES.includes(res.status) });
      }

      const payload = await res.json();
      const raw = typeof payload === 'string' ? payload : payload.raw;
      if (!raw) throw new DesignProviderError('The designer returned an empty response.', { status: 502, retryable: true });
      return raw;
    } catch (err) {
      if (err instanceof DesignProviderError) throw err;
      if (err && err.name === 'AbortError') {
        // Our own timeout, or the caller cancelling: only the former is worth retrying.
        if (signal && signal.aborted) throw new DesignProviderError('Design generation was cancelled.', { retryable: false, cause: err });
        throw new DesignProviderError(`Design generation timed out after ${timeoutMs}ms.`, { status: 408, retryable: true, cause: err });
      }
      throw new DesignProviderError(`Could not reach the design service: ${err && err.message}`, { retryable: true, cause: err });
    } finally {
      if (timer) clearTimeout(timer);
      if (signal && controller) signal.removeEventListener('abort', onAbort);
    }
  };

  return {
    id: `backend:${name}/1`,
    provider: name,
    // The proxy returns one complete document; the seam below is where token
    // streaming lands when the proxy supports it, without changing this contract.
    supportsStreaming: false,

    /**
     * @param {Object} request  from buildDesignRequest.
     * @param {Object} [options] { signal, onDelta }.
     * @returns {Promise<string>} the raw JSON string.
     */
    generate: async (request, { signal, onDelta } = {}) => {
      let lastError = null;
      for (let i = 0; i <= retries; i += 1) {
        try {
          const raw = await attempt(request, signal);
          if (typeof onDelta === 'function') onDelta(raw);
          return raw;
        } catch (err) {
          lastError = err;
          if (!err.retryable || i === retries) throw err;
          if (backoffMs) await sleep(backoffMs * (2 ** i));
        }
      }
      throw lastError;
    },
  };
};

/** True when `provider` satisfies the DesignProvider contract. */
export const isDesignProvider = (provider) =>
  Boolean(provider) && typeof provider === 'object' && typeof provider.generate === 'function';

/** Every provider, built against the same proxy — the abstraction the app selects from. */
export const createProviders = (config = {}) =>
  PROVIDERS.reduce((acc, name) => ({ ...acc, [name]: createBackendProvider(name, config) }), {});
